import { Button } from '@/components/ui/shadcn/button';
import { cn } from '@/lib/utils';
import { Check, Loader2, MapPin, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AddressSearchInput } from '@/components/global/address/AddressSearchInput';

interface InlineAddressCellProps {
    value: string | null | undefined;
    onUpdate?: (value: string, addressData?: { lat?: number; lng?: number; place_id?: string }) => void;
    placeholder?: string;
    className?: string;
    readOnly?: boolean;
}

export function InlineAddressCell({ value, onUpdate, placeholder = 'Enter address...', className, readOnly = false }: InlineAddressCellProps) {
    const normalizedValue = value || '';
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(normalizedValue);
    const [addressData, setAddressData] = useState<{ lat?: number; lng?: number; place_id?: string } | undefined>();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!isEditing) {
            setEditValue(normalizedValue);
        }
    }, [isEditing, normalizedValue]);

    const handleSave = async () => {
        if (editValue === normalizedValue && !addressData) {
            setIsEditing(false);
            return;
        }

        if (!onUpdate) return;

        setIsLoading(true);
        try {
            await onUpdate(editValue, addressData);
            setIsEditing(false);
            setAddressData(undefined);
        } catch (error) {
            console.error('Failed to update address:', error);
            setEditValue(normalizedValue);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setEditValue(normalizedValue);
        setAddressData(undefined);
        setIsEditing(false);
    };

    // Read-only mode: just display the address
    if (readOnly || !onUpdate) {
        return (
            <div
                className={cn('flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm', !normalizedValue && 'text-muted-foreground', className)}
            >
                <MapPin className="text-muted-foreground h-4 w-4 shrink-0" />
                <span className="max-w-[200px] truncate">{normalizedValue || placeholder}</span>
            </div>
        );
    }

    if (isEditing) {
        return (
            <div onClick={(e) => e.stopPropagation()} className={cn('px-2 py-1.5', className)}>
                <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                        <AddressSearchInput
                            value={editValue}
                            onChange={setEditValue}
                            onAddressSelect={(address) => {
                                setEditValue(address.label);
                                setAddressData({
                                    lat: address.lat,
                                    lng: address.lng,
                                    place_id: address.id,
                                });
                            }}
                            placeholder={placeholder}
                            className="h-8"
                        />
                    </div>
                    <Button type="button" size="icon" variant="ghost" onClick={handleSave} disabled={isLoading} className="h-8 w-8 shrink-0">
                        {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    </Button>
                    <Button type="button" size="icon" variant="ghost" onClick={handleCancel} disabled={isLoading} className="h-8 w-8 shrink-0">
                        <X className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
            }}
            className={cn(
                'hover:bg-accent w-full rounded px-2 py-1.5 text-left transition-colors',
                'flex items-center gap-2 text-sm',
                !normalizedValue && 'text-muted-foreground',
                className,
            )}
        >
            <MapPin className="text-muted-foreground h-4 w-4 shrink-0" />
            <span className="max-w-[200px] truncate">{normalizedValue || placeholder}</span>
        </button>
    );
}
